import camelCase from 'lodash/camelCase';
import mapKeys from 'lodash/mapKeys';
import { toUnicode } from 'punycode';
import {
    type BusinessDto,
    type BusinessLiteDto,
    type BusinessModel,
    type ImportSource,
    type BusinessStatItemDto,
    type BusinessStatsDto,
    type BusinessStatsModel,
    type CategoryDto,
    type CategoryModel,
    BusinessType,
} from '../../data-layer/business/types';
import { buildRelativeUrl } from '../../utils/buildUrl';
import type { ClickOutDto } from '../../data-layer/posts/types';
import type { BusinessUI, BusinessDescriptionStatus } from '../../ui/business/types';

export { BusinessDescriptionStatus };

export enum SourceType {
    INSTAGRAM = 'instagram',
}

export enum Role {
    BASE = 'base',
    BLOGGER = 'blogger',
    APPROVED_ADMIN = 'approved_admin',
    SUPER_ADMIN = 'super_admin',
    PENDING_APPROVAL_ADMIN = 'pending_approval_admin',
    SUPPORT = 'support',
    DECLINED_BUSINESS_ADMIN = 'declined_business_admin',
}

export enum BusinessStatus {
    PUBLISHED = 'published',
    DECLINED = 'declined',
    WITHOUT_RANGE = 'without_range',
}

export const getImportSource = (sourceType: SourceType, sources?: ImportSource<SourceType>[]) => {
    const source = sources?.find((s) => s.source === sourceType);

    return source?.url
        ? {
              url: source.url,
              importFormat: source.import_format,
          }
        : undefined;
};

const getStatItem = (businessStatItemDto: BusinessStatItemDto) => ({
    value: businessStatItemDto.value,
    display: businessStatItemDto.display,
    diffValue: businessStatItemDto.diff_value,
    diffDisplay: businessStatItemDto.diff_display,
});

export const toBusinessStatsModel = (statsDto: BusinessStatsDto): BusinessStatsModel => ({
    coverage: {
        total: getStatItem(statsDto.coverage.total),
        subscribers: getStatItem(statsDto.coverage.subscribers),
        notsubscribers: getStatItem(statsDto.coverage.notsubscribers),
        boosted: statsDto.coverage.boosted && getStatItem(statsDto.coverage.boosted),
        organic: statsDto.coverage.organic && getStatItem(statsDto.coverage.organic),
    },
    involved: {
        total: getStatItem(statsDto.involved.total),
        subscribers: getStatItem(statsDto.involved.subscribers),
        notsubscribers: getStatItem(statsDto.involved.notsubscribers),
        boosted: statsDto.involved.boosted && getStatItem(statsDto.involved.boosted),
        organic: statsDto.involved.organic && getStatItem(statsDto.involved.organic),
    },
    shows: {
        total: getStatItem(statsDto.shows.total),
        subscribers: getStatItem(statsDto.shows.subscribers),
        notsubscribers: getStatItem(statsDto.shows.notsubscribers),
        boosted: statsDto.shows.boosted && getStatItem(statsDto.shows.boosted),
        organic: statsDto.shows.organic && getStatItem(statsDto.shows.organic),
    },
    showsInGrid: {
        total: getStatItem(statsDto.shows_in_grid.total),
        subscribers: getStatItem(statsDto.shows_in_grid.subscribers),
        notsubscribers: getStatItem(statsDto.shows_in_grid.notsubscribers),
        boosted: statsDto.shows_in_grid.boosted && getStatItem(statsDto.shows_in_grid.boosted),
        organic: statsDto.shows_in_grid.organic && getStatItem(statsDto.shows_in_grid.organic),
    },
    subscribers: {
        total: getStatItem(statsDto.subscribers.total),
        subscribed: getStatItem(statsDto.subscribers.subscribed),
        unsubscribed: getStatItem(statsDto.subscribers.unsubscribed),
    },
    interactions: {
        total: getStatItem(statsDto.interactions.total),
        clicks: getStatItem(statsDto.interactions.clicks),
        likes: getStatItem(statsDto.interactions.likes),
        shares: getStatItem(statsDto.interactions.shares),
        saves: getStatItem(statsDto.interactions.saves),
    },
    clicks: statsDto.clicks && {
        total: getStatItem(statsDto.clicks.total),
        subscribers: getStatItem(statsDto.clicks.subscribers),
        notsubscribers: getStatItem(statsDto.clicks.notsubscribers),
        boosted: statsDto.clicks.boosted && getStatItem(statsDto.clicks.boosted),
        organic: statsDto.clicks.organic && getStatItem(statsDto.clicks.organic),
    },
    visits: statsDto.visits && {
        total: getStatItem(statsDto.visits.total),
        subscribers: getStatItem(statsDto.visits.subscribers),
        notsubscribers: getStatItem(statsDto.visits.notsubscribers),
        boosted: statsDto.visits.boosted && getStatItem(statsDto.visits.boosted),
        organic: statsDto.visits.organic && getStatItem(statsDto.visits.organic),
    },
    gmv: statsDto.gmv && {
        total: getStatItem(statsDto.gmv.total),
        subscribers: getStatItem(statsDto.gmv.subscribers),
        notsubscribers: getStatItem(statsDto.gmv.notsubscribers),
        boosted: statsDto.gmv.boosted && getStatItem(statsDto.gmv.boosted),
        organic: statsDto.gmv.organic && getStatItem(statsDto.gmv.organic),
    },
    carts: statsDto.carts && {
        total: getStatItem(statsDto.carts.total),
        subscribers: getStatItem(statsDto.carts.subscribers),
        notsubscribers: getStatItem(statsDto.carts.notsubscribers),
        boosted: statsDto.carts.boosted && getStatItem(statsDto.carts.boosted),
        organic: statsDto.carts.organic && getStatItem(statsDto.carts.organic),
    },
    purchases: statsDto.purchases && {
        total: getStatItem(statsDto.purchases.total),
        subscribers: getStatItem(statsDto.purchases.subscribers),
        notsubscribers: getStatItem(statsDto.purchases.notsubscribers),
        boosted: statsDto.purchases.boosted && getStatItem(statsDto.purchases.boosted),
        organic: statsDto.purchases.organic && getStatItem(statsDto.purchases.organic),
    },
});

const DEFAULT_COVER_ID = 'dddf12611932402ea12659064926f94f';
const getCoverUrl = (coverUrl?: string): string | undefined =>
    coverUrl?.includes(DEFAULT_COVER_ID) ? undefined : coverUrl;

const fromCategoryDto = (categoriesDto: CategoryDto[]) =>
    categoriesDto.reduce((acc: Record<number, CategoryModel>, categoryDto) => {
        acc[categoryDto.id] = {
            id: categoryDto.id,
            name: categoryDto.display_name,
        };

        return acc;
    }, {});

const mapClickouts = (clickouts?: ClickOutDto[]) =>
    clickouts?.map((clickout) => mapKeys(clickout, (_, key) => camelCase(key)) as any);

export const fromBusinessDto = (businessDto: BusinessDto): BusinessModel => {
    const isBlogger = businessDto.profile?.type === BusinessType.BLOGGER;
    const accountPrefix = isBlogger ? 'profile' : 'businesses';

    return {
        __type: 'standard',
        url: `/${accountPrefix}/${businessDto.profile.public_id}`,
        statisticUrl: `/${accountPrefix}/${businessDto.profile.public_id}/management/statistic`,
        editLink: `/${accountPrefix}/edit/${businessDto.profile.public_id}`,
        shareUrl:
            businessDto.profile.share_link ||
            buildRelativeUrl(`/${accountPrefix}/${businessDto.profile.public_id}`),
        id: businessDto.profile.id,
        publicId: businessDto.profile.public_id,
        displayName: businessDto.profile.display_name,
        coverUrl: getCoverUrl(businessDto.profile.cover_url),
        avatarUrl: businessDto.profile.avatar_url,
        subscribed: businessDto.subscribed ?? false,
        subscribers: businessDto.profile.subscribers_amount ?? 0,
        posts: businessDto.profile.posts_count ?? 0,
        description: businessDto.profile.description,
        descriptionStatus: businessDto.profile.description_status,
        links: mapKeys(businessDto.profile.links ?? {}, (_, key) => camelCase(key)),
        role: businessDto.role,
        createdAt: businessDto.profile.created_at,
        hideInFeed: businessDto.profile.hide_in_feed,
        offerAgreement: businessDto.profile?.offer_agreement,
        instagram: getImportSource(
            SourceType.INSTAGRAM,
            businessDto.profile?.import_settings?.sources,
        ),
        status: businessDto.profile.status,
        statusDisplay: businessDto.profile.status_display,
        categories: businessDto.profile.display_categories
            ? fromCategoryDto(businessDto.profile.display_categories)
            : {},
        showProductsImportSettings: businessDto.profile.show_products_import_settings,
        useProductsAutoImport: businessDto.profile.use_products_auto_import,
        useProductsDisableAutoParser: businessDto.profile.disabled_offer_sources?.includes(1),
        siteUrl: businessDto.profile.site_url,
        shortSite: toUnicode(
            businessDto.profile.short_site || businessDto.profile.links?.short_site || '',
        ),
        catalogTitle: businessDto.profile.catalog_info?.button_title || 'Каталог',
        catalogUrl: businessDto.profile.catalog_info?.catalog_id
            ? `/${accountPrefix}/${businessDto.profile.public_id}/catalog/${businessDto.profile.catalog_info.catalog_id}`
            : undefined,
        type: businessDto.profile?.type,
        allowedToBoosts: businessDto.profile.allowed_to_boosts,
        reviewsInfo: businessDto.profile.review_info
            ? {
                  rating: businessDto.profile.review_info.rating,
                  reviews: businessDto.profile.review_info.review_count,
                  url: businessDto.profile.review_info.review_url,
              }
            : undefined,
        displayGeotargeting: businessDto.profile.display_geotargeting,
        isService: businessDto.profile.is_service,
        isDikidiApproved:
            businessDto.profile.is_dikidi_approved ||
            Boolean(businessDto.profile.is_service && businessDto.profile.links.dikidi_url), // хак, бэкенд не прокидывает is_dikidi_approved в ручку
        clickouts: mapClickouts(businessDto.profile.clickouts),
    };
};

export const fromBusinessLiteDto = (businessDto: BusinessLiteDto): BusinessModel => {
    const isBlogger = businessDto.type === BusinessType.BLOGGER;
    const accountPrefix = isBlogger ? 'profile' : 'businesses';

    return {
        __type: 'lite',
        url: `/${accountPrefix}/${businessDto.public_id}`,
        statisticUrl: `/${accountPrefix}/${businessDto.public_id}/management/statistic`,
        editLink: `/${accountPrefix}/edit/${businessDto.public_id}`,
        shareUrl:
            businessDto.share_link ||
            buildRelativeUrl(`/${accountPrefix}/${businessDto.public_id}`),
        id: businessDto.id,
        publicId: businessDto.public_id,
        avatarUrl: businessDto.avatar_url,
        displayName: businessDto.display_name,
        description: businessDto.description,
        descriptionStatus: businessDto.description_status,
        subscribed: businessDto.subscribed || false,
        role: businessDto.role,
        links: {},
        siteUrl: businessDto.site_url,
        shortSite: toUnicode(businessDto.short_site || ''),
        catalogTitle: businessDto.catalog_info?.button_title || 'Каталог',
        catalogUrl: businessDto.catalog_info?.catalog_id
            ? `/${accountPrefix}/${businessDto.public_id}/catalog/${businessDto.catalog_info.catalog_id}`
            : undefined,
        type: businessDto.type,
        allowedToBoosts: businessDto.allowed_to_boosts,
        isDikidiApproved: businessDto.is_dikidi_approved,
        isService: businessDto.is_service,
        clickouts: mapClickouts(businessDto.clickouts),
    };
};

export const fromBusinessUI = (businessUI: BusinessUI): BusinessModel =>
    ({
        __type: 'standard',
        ...businessUI,
    }) as BusinessModel;
